import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Calendar,
  Clock,
  User,
  Stethoscope,
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Phone,
  PartyPopper,
  CheckCircle
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to format time to AM/PM
const formatTimeToAMPM = (time24) => {
  if (!time24 || time24 === 'queue') return time24;
  
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

const PatientReschedulePage = () => {
  const { appointmentId, token } = useParams();
  
  // States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [appointmentDetails, setAppointmentDetails] = useState(null);
  const [clinicName, setClinicName] = useState('');
  const [alreadyActioned, setAlreadyActioned] = useState(false);
  const [actionedStatus, setActionedStatus] = useState('');
  
  // Slots states
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [serviceName, setServiceName] = useState('');
  const [serviceType, setServiceType] = useState('');
  
  // Selection states
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [currentWeek, setCurrentWeek] = useState(0);
  
  // Submit states
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [newAppointment, setNewAppointment] = useState(null);
  
  // Callback states
  const [showCallback, setShowCallback] = useState(false);
  const [callbackNotes, setCallbackNotes] = useState('');
  const [callbackSubmitting, setCallbackSubmitting] = useState(false);
  const [callbackSuccess, setCallbackSuccess] = useState(false);

  // Fetch appointment and available slots
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get appointment details
        const aptRes = await axios.get(`${API_URL}/api/patient/appointment/${appointmentId}/${token}`);
        
        if (aptRes.data.already_actioned) {
          setAlreadyActioned(true);
          setActionedStatus(aptRes.data.status);
          setLoading(false);
          return;
        }

        setAppointmentDetails(aptRes.data.appointment);
        setClinicName(aptRes.data.clinic_name);

        // Get available slots
        setSlotsLoading(true);
        const slotsRes = await axios.get(`${API_URL}/api/patient/available-slots/${appointmentId}/${token}`);
        setAvailableSlots(slotsRes.data.available_slots || []);
        setServiceName(slotsRes.data.service_name);
        setServiceType(slotsRes.data.service_type);

      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.response?.data?.detail || 'Invalid or expired link. Please contact the clinic.');
      } finally {
        setLoading(false);
        setSlotsLoading(false);
      }
    };

    fetchData();
  }, [appointmentId, token]);

  // Group slots by week
  const groupedByWeek = availableSlots.reduce((acc, slot, index) => {
    const weekIndex = Math.floor(index / 7);
    if (!acc[weekIndex]) acc[weekIndex] = [];
    acc[weekIndex].push(slot);
    return acc;
  }, []);

  const currentWeekSlots = groupedByWeek[currentWeek] || [];
  const totalWeeks = groupedByWeek.length;

  // Handle reschedule submit
  const handleReschedule = async () => {
    if (!selectedDate || !selectedTime) {
      setError('Please select a date and time');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const res = await axios.post(`${API_URL}/api/patient/reschedule/${appointmentId}/${token}`, {
        new_date: selectedDate,
        new_time: selectedTime
      });

      if (res.data.success) {
        setSuccess(true);
        setNewAppointment(res.data.new_appointment);
      } else {
        setError(res.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reschedule appointment');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle callback request
  const handleCallbackRequest = async () => {
    setCallbackSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/patient/callback-request/${appointmentId}/${token}`, {
        preferred_dates: [],
        notes: callbackNotes
      });

      if (res.data.success) {
        setCallbackSuccess(true);
      } else {
        setError(res.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit callback request');
    } finally {
      setCallbackSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading available slots...</p>
        </div>
      </div>
    );
  }

  // Error state (initial load)
  if (error && !appointmentDetails) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Oops!</h2>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Already actioned state
  if (alreadyActioned) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Already {actionedStatus}</h2>
            <p className="text-gray-600">This appointment has already been {actionedStatus}.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Callback success
  if (callbackSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Phone className="w-10 h-10 text-purple-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Callback Requested!</h2>
            <p className="text-gray-600 mb-6">Our team will call you shortly to help schedule your appointment.</p>
            <div className="p-4 bg-purple-50 rounded-lg">
              <p className="text-sm text-purple-800">You can close this window now.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Reschedule success
  if (success && newAppointment) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <PartyPopper className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Rescheduled!</h2>
            <p className="text-gray-600 mb-6">Your appointment has been rescheduled successfully.</p>
            
            <div className="bg-green-50 rounded-lg p-4 text-left space-y-2 mb-6">
              <div className="flex items-center gap-2 text-gray-700">
                <Calendar className="w-4 h-4 text-green-600" />
                <span className="font-medium">{newAppointment.formatted_date}</span>
              </div>
              {newAppointment.time && newAppointment.time !== 'queue' && (
                <div className="flex items-center gap-2 text-gray-700">
                  <Clock className="w-4 h-4 text-green-600" />
                  <span>{formatTimeToAMPM(newAppointment.time)}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-gray-700">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-green-700 font-medium">Confirmed</span>
              </div>
              <Badge variant="outline" className="mt-2">
                New Ref: {newAppointment.ref_number}
              </Badge>
            </div>

            <div className="p-4 bg-gray-100 rounded-lg">
              <p className="text-sm text-gray-600">You can close this window now.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Callback form
  if (showCallback) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 p-4">
        <div className="max-w-lg mx-auto">
          <Button
            variant="ghost"
            onClick={() => setShowCallback(false)}
            className="mb-4"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Back to slots
          </Button>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-purple-600">
                <Phone className="w-6 h-6" />
                Request Callback
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-600">
                Can&apos;t find a suitable slot? Our team will call you to help find the perfect time.
              </p>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Any preferences? (optional)
                </label>
                <textarea
                  value={callbackNotes}
                  onChange={(e) => setCallbackNotes(e.target.value)}
                  placeholder="E.g., Mornings work best for me, or I prefer weekends..."
                  className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                  rows={4}
                />
              </div>

              <Button
                onClick={handleCallbackRequest}
                disabled={callbackSubmitting}
                className="w-full h-12 bg-purple-600 hover:bg-purple-700"
              >
                {callbackSubmitting ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <Phone className="w-5 h-5 mr-2" />
                )}
                Request Callback
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Main reschedule form
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">{clinicName}</h1>
          <p className="text-gray-600">Reschedule Appointment</p>
        </div>

        {/* Current Appointment */}
        {appointmentDetails && (
          <Card className="mb-6 border-orange-200 bg-orange-50">
            <CardContent className="p-4">
              <p className="text-xs text-orange-600 font-medium mb-2">CURRENT APPOINTMENT</p>
              <div className="flex items-center gap-4">
                <div>
                  <p className="font-medium text-gray-900">{appointmentDetails.service_name}</p>
                  <p className="text-sm text-gray-600">
                    {new Date(appointmentDetails.scheduled_date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                    {appointmentDetails.scheduled_time && ` at ${formatTimeToAMPM(appointmentDetails.scheduled_time)}`}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Available Slots */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center justify-between">
              <span>Select New Date & Time</span>
              {serviceName && (
                <Badge variant="outline" className="text-xs">
                  {serviceName}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <div className="p-3 bg-red-100 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}

            {slotsLoading ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
                <p className="text-gray-600">Loading slots...</p>
              </div>
            ) : availableSlots.length === 0 ? (
              <div className="text-center py-8">
                <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">No available slots for the next 30 days.</p>
                <Button
                  onClick={() => setShowCallback(true)}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Request Callback
                </Button>
              </div>
            ) : (
              <>
                {/* Week Navigation */}
                <div className="flex items-center justify-between bg-gray-50 rounded-lg p-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentWeek(Math.max(0, currentWeek - 1))}
                    disabled={currentWeek === 0}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm font-medium text-gray-600">
                    Week {currentWeek + 1} of {totalWeeks}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentWeek(Math.min(totalWeeks - 1, currentWeek + 1))}
                    disabled={currentWeek >= totalWeeks - 1}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>

                {/* Date Selection */}
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Choose Date</label>
                  <div className="grid grid-cols-2 gap-2">
                    {currentWeekSlots.map((slot) => (
                      <button
                        key={slot.date}
                        onClick={() => {
                          setSelectedDate(slot.date);
                          setSelectedTime(null);
                          setError(null);
                        }}
                        className={`p-3 text-left rounded-lg border-2 transition-all ${
                          selectedDate === slot.date
                            ? 'border-blue-500 bg-blue-50 shadow-sm'
                            : 'border-gray-200 hover:border-blue-200 hover:bg-gray-50'
                        }`}
                      >
                        <div className={`font-semibold ${selectedDate === slot.date ? 'text-blue-700' : 'text-gray-900'}`}>
                          {slot.formatted_date}
                        </div>
                        <div className="text-xs text-gray-500">{slot.day_name}</div>
                        <div className="text-xs text-green-600 mt-1">
                          {slot.slots.length === 1 && slot.slots[0] === 'queue' 
                            ? 'Queue available' 
                            : `${slot.slots.length} slots`}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Time Selection */}
                {selectedDate && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">Choose Time</label>
                    <div className="grid grid-cols-3 gap-2">
                      {availableSlots
                        .find((s) => s.date === selectedDate)
                        ?.slots.map((time) => (
                          <button
                            key={time}
                            onClick={() => {
                              setSelectedTime(time);
                              setError(null);
                            }}
                            className={`p-2 text-center rounded-lg border-2 transition-all text-sm ${
                              selectedTime === time
                                ? 'border-blue-500 bg-blue-50 text-blue-700 font-semibold'
                                : 'border-gray-200 hover:border-blue-200'
                            }`}
                          >
                            {time === 'queue' ? 'Queue' : formatTimeToAMPM(time)}
                          </button>
                        ))}
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <Button
                  onClick={handleReschedule}
                  disabled={submitting || !selectedDate || !selectedTime}
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-lg"
                  data-testid="submit-reschedule-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <Calendar className="w-5 h-5 mr-2" />
                  )}
                  Confirm Reschedule
                </Button>

                {/* Callback Option */}
                <div className="text-center pt-2 border-t">
                  <button
                    onClick={() => setShowCallback(true)}
                    className="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1 mx-auto"
                  >
                    <Phone className="w-4 h-4" />
                    Can&apos;t find a slot? Request callback
                  </button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PatientReschedulePage;
